import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Link,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import UploadFileOutlinedIcon from "@mui/icons-material/UploadFileOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { useTranslation } from "react-i18next";
import { fetchData } from "../../api/client";
import {
  buildCieloMtlsCommonName,
  buildCieloMtlsMailto,
  CIELO_PIX_MTLS_SUPPORT_EMAIL,
  downloadTextFile,
  slugifyCieloMtlsPartnerName,
} from "../../utils/cieloPixMtlsCsr";

function readTextFile(file) {
  return new Promise((resolve, reject) => {
    if (!file) {
      reject(new Error("empty"));
      return;
    }
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ""));
    reader.onerror = () => reject(reader.error || new Error("read failed"));
    reader.readAsText(file);
  });
}

function partnerFieldHelperText(t, p, slug) {
  const cn = buildCieloMtlsCommonName(slug);
  if (cn) return t(`${p}.partnerHelperWithCn`, { cn });
  return t(`${p}.partnerHelper`);
}

function StepCard({ step, title, children, done }) {
  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        borderRadius: 2,
        bgcolor: done ? "success.50" : "background.paper",
        borderColor: done ? "success.light" : "divider",
      }}
    >
      <Stack direction="row" spacing={1.5} alignItems="flex-start">
        <Box
          sx={{
            width: 28,
            height: 28,
            borderRadius: "50%",
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontWeight: 700,
            fontSize: 14,
            bgcolor: done ? "success.main" : "primary.main",
            color: "primary.contrastText",
          }}
        >
          {done ? <CheckCircleOutlineIcon sx={{ fontSize: 18, color: "#fff" }} /> : step}
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle2" sx={{ mb: 0.75, fontWeight: 700 }}>
            {title}
          </Typography>
          {children}
        </Box>
      </Stack>
    </Paper>
  );
}

/**
 * Fluxo mTLS Cielo:
 * - 1º download: gera e guarda CSR + chave
 * - Downloads seguintes: devolve o MESMO CSR (não invalida o pedido à Cielo)
 * - Regenerar: só com confirmação explícita (nova chave; limpa certificado)
 */
export default function CieloPixMtlsCsrPanel({
  suggestedPartnerSlug = "",
  suggestedPartnerLabel = "",
  paymentMethodId = null,
  hasStoredKey = false,
  hasStoredCert = false,
  hasStoredCsr = false,
  onPrivateKeyPem,
  onCsrDownloaded,
}) {
  const { t } = useTranslation("settings");
  const p = "paymentMethodsPage.cieloPixMtlsCsr";

  const initialSlug = slugifyCieloMtlsPartnerName(suggestedPartnerSlug);
  const [partnerSlug, setPartnerSlug] = useState(initialSlug);
  const [showCustomName, setShowCustomName] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [csrDone, setCsrDone] = useState(false);
  const [pendingSave, setPendingSave] = useState(false);
  const [confirmRegenerateOpen, setConfirmRegenerateOpen] = useState(false);
  const [localHasCsr, setLocalHasCsr] = useState(!!hasStoredCsr);
  const [localHasKey, setLocalHasKey] = useState(!!hasStoredKey);
  const [localHasCert, setLocalHasCert] = useState(!!hasStoredCert);
  const [legacyNeedsRegenerate, setLegacyNeedsRegenerate] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [customCsrPem, setCustomCsrPem] = useState("");
  const [customKeyPem, setCustomKeyPem] = useState("");
  const [customCsrFileName, setCustomCsrFileName] = useState("");
  const [customKeyFileName, setCustomKeyFileName] = useState("");
  const [confirmImportOpen, setConfirmImportOpen] = useState(false);

  useEffect(() => {
    const next = slugifyCieloMtlsPartnerName(suggestedPartnerSlug);
    if (next && !showCustomName) setPartnerSlug(next);
  }, [suggestedPartnerSlug, showCustomName]);

  useEffect(() => {
    setLocalHasCsr(!!hasStoredCsr);
    setLocalHasKey(!!hasStoredKey);
    setLocalHasCert(!!hasStoredCert);
    if (hasStoredCsr || hasStoredKey) setCsrDone(true);
  }, [hasStoredCsr, hasStoredKey, hasStoredCert]);

  const effectiveSlug = useMemo(
    () => slugifyCieloMtlsPartnerName(partnerSlug),
    [partnerSlug]
  );

  const canGenerate = effectiveSlug.length >= 2;
  const alreadyHasFile = localHasCsr || (csrDone && localHasKey);
  const canOpenEmail = alreadyHasFile || csrDone;

  const applySuccess = useCallback(
    (data) => {
      const csrPem = data?.csrPem;
      if (!csrPem) throw new Error(t(`${p}.generateFailed`));

      downloadTextFile("certificado.csr", csrPem);
      setCsrDone(true);
      setLegacyNeedsRegenerate(false);

      if (data.reused) {
        setLocalHasCsr(true);
        setLocalHasKey(true);
        setPendingSave(false);
      } else if (data.privateKeyStored) {
        setLocalHasCsr(true);
        setLocalHasKey(true);
        if (data.certCleared || data.regenerated || data.imported) {
          setLocalHasCert(false);
        }
        setPendingSave(false);
        if (typeof onPrivateKeyPem === "function") {
          onPrivateKeyPem(null, {
            storedOnServer: true,
            regenerated: !!data.regenerated,
            imported: !!data.imported,
            certCleared: !!data.certCleared || !!data.regenerated || !!data.imported,
            hasCsr: true,
          });
        }
      } else if (data.privateKeyPem && typeof onPrivateKeyPem === "function") {
        setLocalHasKey(true);
        setLocalHasCsr(true);
        setPendingSave(true);
        onPrivateKeyPem(data.privateKeyPem, {
          storedOnServer: false,
          regenerated: !!data.regenerated,
          imported: !!data.imported,
          hasCsr: true,
          csrPem: data.csrPem,
          cn: data.cn,
          partnerSlug: effectiveSlug,
          generatedAt: data.generatedAt || new Date().toISOString(),
          csrSource: data.imported ? "custom" : "generated",
        });
      }

      if (data.imported) {
        setCustomCsrPem("");
        setCustomKeyPem("");
        setCustomCsrFileName("");
        setCustomKeyFileName("");
        setShowAdvanced(false);
      }

      if (typeof onCsrDownloaded === "function") onCsrDownloaded(data);
    },
    [onPrivateKeyPem, onCsrDownloaded, t, p, effectiveSlug]
  );

  const requestCsr = useCallback(
    async ({ regenerate = false } = {}) => {
      setErr(null);
      if (!canGenerate) {
        setErr(t(`${p}.partnerRequired`));
        return;
      }
      if (!paymentMethodId && regenerate) {
        // sem id ainda: sempre gera novo (chave vem no response para salvar depois)
      }
      setBusy(true);
      try {
        const resp = await fetchData("/payment-methods/cielo-pix-mtls-csr", "POST", {
          partnerSlug: effectiveSlug,
          ...(paymentMethodId ? { id: paymentMethodId } : {}),
          ...(regenerate ? { regenerate: true } : {}),
        });
        const data = resp?.data ?? resp;
        applySuccess(data);
      } catch (e) {
        const code = e?.data?.code || e?.code;
        if (
          e?.status === 409 ||
          code === "CSR_LEGACY_NEEDS_REGENERATE" ||
          (typeof e?.message === "string" && e.message.includes("CSR_LEGACY"))
        ) {
          setLegacyNeedsRegenerate(true);
          setLocalHasKey(true);
          setCsrDone(true);
          setErr(null);
          setConfirmRegenerateOpen(true);
          return;
        }
        const msg =
          (e && typeof e === "object" && e.message) ||
          (typeof e === "string" ? e : null) ||
          t(`${p}.generateFailed`);
        setErr(msg);
        if (!alreadyHasFile) setCsrDone(false);
      } finally {
        setBusy(false);
      }
    },
    [
      canGenerate,
      effectiveSlug,
      paymentMethodId,
      applySuccess,
      alreadyHasFile,
      t,
      p,
    ]
  );

  const handlePrimaryDownload = useCallback(() => {
    // Já tem CSR guardado → só redownload (sem regenerate)
    if (localHasCsr || (paymentMethodId && localHasKey && !legacyNeedsRegenerate)) {
      requestCsr({ regenerate: false });
      return;
    }
    requestCsr({ regenerate: false });
  }, [localHasCsr, localHasKey, paymentMethodId, legacyNeedsRegenerate, requestCsr]);

  const handleConfirmRegenerate = useCallback(async () => {
    setConfirmRegenerateOpen(false);
    await requestCsr({ regenerate: true });
  }, [requestCsr]);

  const canImportCustom =
    String(customCsrPem || "").trim().length > 40 &&
    String(customKeyPem || "").trim().length > 40;

  const runImportCustom = useCallback(async () => {
    setConfirmImportOpen(false);
    setErr(null);
    setBusy(true);
    try {
      const resp = await fetchData("/payment-methods/cielo-pix-mtls-csr", "POST", {
        mode: "import",
        importCustom: true,
        partnerSlug: effectiveSlug || "parceiro",
        csrPem: customCsrPem,
        privateKeyPem: customKeyPem,
        ...(paymentMethodId ? { id: paymentMethodId } : {}),
      });
      const data = resp?.data ?? resp;
      applySuccess(data);
    } catch (e) {
      const msg =
        (e && typeof e === "object" && e.message) ||
        (typeof e === "string" ? e : null) ||
        t(`${p}.importFailed`);
      setErr(msg);
    } finally {
      setBusy(false);
    }
  }, [
    effectiveSlug,
    customCsrPem,
    customKeyPem,
    paymentMethodId,
    applySuccess,
    t,
    p,
  ]);

  const handleImportClick = useCallback(() => {
    if (!canImportCustom) {
      setErr(t(`${p}.importRequired`));
      return;
    }
    if (alreadyHasFile || localHasKey || localHasCert) {
      setConfirmImportOpen(true);
      return;
    }
    runImportCustom();
  }, [
    canImportCustom,
    alreadyHasFile,
    localHasKey,
    localHasCert,
    runImportCustom,
    t,
    p,
  ]);

  const handlePickCsrFile = useCallback(
    async (e) => {
      const file = e.target.files?.[0];
      e.target.value = "";
      if (!file) return;
      try {
        const text = await readTextFile(file);
        setCustomCsrPem(text);
        setCustomCsrFileName(file.name);
        setErr(null);
      } catch {
        setErr(t(`${p}.importReadFailed`));
      }
    },
    [t, p]
  );

  const handlePickKeyFile = useCallback(
    async (e) => {
      const file = e.target.files?.[0];
      e.target.value = "";
      if (!file) return;
      try {
        const text = await readTextFile(file);
        setCustomKeyPem(text);
        setCustomKeyFileName(file.name);
        setErr(null);
      } catch {
        setErr(t(`${p}.importReadFailed`));
      }
    },
    [t, p]
  );

  const mailtoHref = canGenerate ? buildCieloMtlsMailto(effectiveSlug) : undefined;

  const statusSeverity = localHasCert ? "success" : alreadyHasFile || localHasKey ? "info" : "warning";
  const statusText = localHasCert
    ? t(`${p}.statusCertOk`)
    : alreadyHasFile || localHasKey
      ? t(`${p}.statusWaitingCert`)
      : t(`${p}.statusNeedCsr`);

  const primaryLabel = busy
    ? t(`${p}.generating`)
    : localHasCsr || (csrDone && localHasKey && !legacyNeedsRegenerate)
      ? t(`${p}.redownloadCsr`)
      : t(`${p}.downloadCsr`);

  return (
    <Stack spacing={2} sx={{ mb: 1 }}>
      <Typography variant="body2" color="text.secondary">
        {t(`${p}.intro`)}
      </Typography>

      <Alert severity={statusSeverity} icon={false} sx={{ py: 1 }}>
        <Typography variant="body2" component="div" sx={{ fontWeight: 600 }}>
          {statusText}
        </Typography>
        {(alreadyHasFile || localHasKey) && !localHasCert ? (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {t(`${p}.statusWaitingCertHint`)}
          </Typography>
        ) : null}
        {localHasCert ? (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {t(`${p}.statusCertOkHint`)}
          </Typography>
        ) : null}
      </Alert>

      {suggestedPartnerLabel && !showCustomName ? (
        <Alert severity="info" icon={false} sx={{ py: 1 }}>
          <Typography variant="body2" component="div">
            {t(`${p}.usingCompany`, {
              name: suggestedPartnerLabel,
              cn: buildCieloMtlsCommonName(effectiveSlug) || "—",
            })}
          </Typography>
        </Alert>
      ) : null}

      <StepCard
        step={1}
        title={t(`${p}.step1Title`)}
        done={alreadyHasFile || localHasKey}
      >
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          {localHasCsr
            ? t(`${p}.step1BodyRedownload`)
            : t(`${p}.step1Body`)}
        </Typography>

        {!showCustomName && suggestedPartnerLabel ? (
          <Button
            size="small"
            sx={{ mb: 1.5, textTransform: "none" }}
            onClick={() => setShowCustomName(true)}
            disabled={busy}
          >
            {t(`${p}.changeName`)}
          </Button>
        ) : null}

        <Collapse in={showCustomName || !suggestedPartnerLabel}>
          <TextField
            label={t(`${p}.partnerLabel`)}
            placeholder={t(`${p}.partnerPlaceholder`)}
            value={partnerSlug}
            onChange={(e) => {
              setPartnerSlug(
                e.target.value.replace(/[^a-zA-Z0-9]/gi, "").slice(0, 48)
              );
            }}
            fullWidth
            size="small"
            sx={{ mb: 1.5 }}
            helperText={partnerFieldHelperText(t, p, effectiveSlug)}
            disabled={busy || (localHasCsr && !legacyNeedsRegenerate)}
          />
        </Collapse>

        <Button
          variant="contained"
          size="large"
          disabled={busy || !canGenerate}
          startIcon={<DownloadOutlinedIcon />}
          onClick={handlePrimaryDownload}
          fullWidth
        >
          {primaryLabel}
        </Button>

        {(alreadyHasFile || localHasKey) && paymentMethodId ? (
          <Button
            size="small"
            color="warning"
            sx={{ mt: 1.5, textTransform: "none" }}
            startIcon={<RefreshOutlinedIcon />}
            disabled={busy}
            onClick={() => setConfirmRegenerateOpen(true)}
          >
            {t(`${p}.regenerateCsr`)}
          </Button>
        ) : null}

        {legacyNeedsRegenerate ? (
          <Alert severity="warning" sx={{ mt: 1.5 }}>
            {t(`${p}.legacyNeedsRegenerate`)}
          </Alert>
        ) : null}

        {pendingSave ? (
          <Alert severity="warning" sx={{ mt: 1.5 }}>
            {t(`${p}.saveReminder`)}
          </Alert>
        ) : null}

        {localHasCsr ? (
          <Alert severity="success" icon={false} sx={{ mt: 1.5, py: 1 }}>
            <Typography variant="body2">{t(`${p}.sameFileHint`)}</Typography>
          </Alert>
        ) : null}

        <Box sx={{ mt: 2 }}>
          <Button
            size="small"
            sx={{ textTransform: "none" }}
            endIcon={showAdvanced ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            onClick={() => setShowAdvanced((v) => !v)}
            disabled={busy}
          >
            {t(`${p}.advancedToggle`)}
          </Button>
          <Collapse in={showAdvanced}>
            <Paper variant="outlined" sx={{ p: 2, mt: 1, bgcolor: "grey.50" }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                {t(`${p}.advancedIntro`)}
              </Typography>
              <Alert severity="info" icon={false} sx={{ mb: 2, py: 1 }}>
                <Typography variant="body2">{t(`${p}.advancedKeyRequired`)}</Typography>
              </Alert>

              <Stack spacing={1.5}>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems="center">
                  <Button component="label" variant="outlined" startIcon={<UploadFileOutlinedIcon />} disabled={busy}>
                    {t(`${p}.uploadCsr`)}
                    <input
                      type="file"
                      hidden
                      accept=".csr,.pem,.txt,application/pkcs10,application/x-pem-file,text/plain"
                      onChange={handlePickCsrFile}
                    />
                  </Button>
                  <Typography variant="caption" color="text.secondary">
                    {customCsrFileName || t(`${p}.uploadCsrHint`)}
                  </Typography>
                </Stack>
                <TextField
                  label={t(`${p}.customCsrLabel`)}
                  value={customCsrPem}
                  onChange={(e) => {
                    setCustomCsrPem(e.target.value);
                    setCustomCsrFileName("");
                  }}
                  fullWidth
                  multiline
                  minRows={3}
                  size="small"
                  disabled={busy}
                  placeholder="-----BEGIN CERTIFICATE REQUEST-----"
                />

                <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems="center">
                  <Button component="label" variant="outlined" startIcon={<UploadFileOutlinedIcon />} disabled={busy}>
                    {t(`${p}.uploadKey`)}
                    <input
                      type="file"
                      hidden
                      accept=".key,.pem,.txt,application/x-pem-file,text/plain"
                      onChange={handlePickKeyFile}
                    />
                  </Button>
                  <Typography variant="caption" color="text.secondary">
                    {customKeyFileName || t(`${p}.uploadKeyHint`)}
                  </Typography>
                </Stack>
                <TextField
                  label={t(`${p}.customKeyLabel`)}
                  value={customKeyPem}
                  onChange={(e) => {
                    setCustomKeyPem(e.target.value);
                    setCustomKeyFileName("");
                  }}
                  fullWidth
                  multiline
                  minRows={3}
                  size="small"
                  disabled={busy}
                  placeholder="-----BEGIN PRIVATE KEY-----"
                />

                <Button
                  variant="contained"
                  color="secondary"
                  disabled={busy || !canImportCustom}
                  startIcon={<UploadFileOutlinedIcon />}
                  onClick={handleImportClick}
                >
                  {busy ? t(`${p}.generating`) : t(`${p}.importSubmit`)}
                </Button>
              </Stack>
            </Paper>
          </Collapse>
        </Box>
      </StepCard>

      <StepCard step={2} title={t(`${p}.step2Title`)} done={false}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          {t(`${p}.step2Body`, { email: CIELO_PIX_MTLS_SUPPORT_EMAIL })}
        </Typography>
        <Button
          variant="outlined"
          size="large"
          fullWidth
          disabled={!canOpenEmail}
          startIcon={<EmailOutlinedIcon />}
          component="a"
          href={canOpenEmail ? mailtoHref : undefined}
          target="_blank"
          rel="noopener noreferrer"
        >
          {t(`${p}.openEmail`)}
        </Button>
        {!canOpenEmail ? (
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
            {t(`${p}.emailDisabledHint`)}
          </Typography>
        ) : (
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
            {t(`${p}.emailAttachHint`)}
          </Typography>
        )}
      </StepCard>

      {err ? (
        <Alert severity="error" onClose={() => setErr(null)}>
          {err}
        </Alert>
      ) : null}

      <Typography variant="caption" color="text.secondary">
        {t(`${p}.supportFooter`)}{" "}
        <Link href={`mailto:${CIELO_PIX_MTLS_SUPPORT_EMAIL}`}>
          {CIELO_PIX_MTLS_SUPPORT_EMAIL}
        </Link>
      </Typography>

      <Dialog
        open={confirmRegenerateOpen}
        onClose={() => !busy && setConfirmRegenerateOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t(`${p}.regenerateTitle`)}</DialogTitle>
        <DialogContent>
          <DialogContentText component="div">
            <Typography variant="body2" paragraph>
              {t(`${p}.regenerateBody`)}
            </Typography>
            {localHasCert || hasStoredCert ? (
              <Alert severity="error" sx={{ mb: 1 }}>
                {t(`${p}.regenerateClearsCert`)}
              </Alert>
            ) : null}
            <Typography variant="body2" color="text.secondary">
              {t(`${p}.regenerateBody2`)}
            </Typography>
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setConfirmRegenerateOpen(false)} disabled={busy}>
            {t(`${p}.regenerateCancel`)}
          </Button>
          <Button
            color="warning"
            variant="contained"
            onClick={handleConfirmRegenerate}
            disabled={busy}
            startIcon={<RefreshOutlinedIcon />}
          >
            {t(`${p}.regenerateConfirm`)}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={confirmImportOpen}
        onClose={() => !busy && setConfirmImportOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t(`${p}.importConfirmTitle`)}</DialogTitle>
        <DialogContent>
          <DialogContentText component="div">
            <Typography variant="body2" paragraph>
              {t(`${p}.importConfirmBody`)}
            </Typography>
            {localHasCert || hasStoredCert ? (
              <Alert severity="error" sx={{ mb: 1 }}>
                {t(`${p}.regenerateClearsCert`)}
              </Alert>
            ) : null}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setConfirmImportOpen(false)} disabled={busy}>
            {t(`${p}.regenerateCancel`)}
          </Button>
          <Button
            color="secondary"
            variant="contained"
            onClick={runImportCustom}
            disabled={busy}
            startIcon={<UploadFileOutlinedIcon />}
          >
            {t(`${p}.importConfirm`)}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
